/**
 * Баланс счёта по конторе. Правда — касса `/kassa`: карандаш в сите, студия и
 * бот пишут остаток туда, сервер сводит остатки обратно в профиль (`caps`).
 *
 * Сохранение сразу ставит число в настройки, чтобы лента пересчиталась без
 * ожидания сети. Отказ сервера возвращает то, что лежит в кассе, и называет
 * причину: два счёта одной конторы, например, правятся только в кассе.
 */

import { apiUrl } from '../lib/build';
import { EXPERIMENTS, TRANSFERS } from '../lib/feedContext';
import { useSettings } from '../store/settings';

/** Как часто сверять остатки, пока вкладка открыта. Касса меняет их сделками. */
const REFRESH_MS = 60_000;

type Cur = 'RUB' | 'USD';

interface Wire {
  caps?: Record<string, number | string>;
  capsCur?: Record<string, string>;
  detail?: string;
}

function adopt(body: Wire): void {
  if (body.caps === undefined || body.caps === null || typeof body.caps !== 'object') return;
  const caps: Record<string, number> = {};
  for (const [code, raw] of Object.entries(body.caps)) {
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) caps[code] = n;
  }
  const curs: Record<string, Cur> = {};
  for (const [code, raw] of Object.entries(body.capsCur ?? {})) {
    if (raw === 'RUB' || raw === 'USD') curs[code] = raw;
  }
  useSettings.getState().setBalances(caps, curs);
}

/** Остатки из кассы. `false` — сервер молчит или входа нет; настройки не трогаем. */
export async function pullBalances(): Promise<boolean> {
  if (EXPERIMENTS || TRANSFERS) return false;
  try {
    const res = await fetch(apiUrl('/v1/book/balance'), { credentials: 'same-origin' });
    if (!res.ok) return false;
    adopt((await res.json()) as Wire);
    return true;
  } catch {
    return false;
  }
}

/**
 * Записать баланс конторы. `amount` — строка с точкой в валюте `cur`; `null` —
 * снять баланс (если остаток ведёт касса, сервер откажет и скажет почему).
 * Ответ — `null`, если всё записано, иначе слова для человека.
 */
export async function saveBalance(
  venue: string,
  amount: string | null,
  cur: Cur,
  name: string,
): Promise<string | null> {
  const before = useSettings.getState();
  const value = amount === null ? null : Number(amount);
  before.setCap(venue, value, cur);
  if (EXPERIMENTS || TRANSFERS) return null;
  try {
    const res = await fetch(apiUrl('/v1/book/balance'), {
      method: 'PUT',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        venue,
        amount,
        currency: cur,
        name,
        fx: before.fx > 0 ? before.fx.toFixed(2) : undefined,
      }),
    });
    const body = (await res.json().catch(() => ({}))) as Wire;
    if (!res.ok) {
      await pullBalances();
      return typeof body.detail === 'string' && body.detail !== '' ? body.detail : 'касса не приняла баланс';
    }
    adopt(body);
    return null;
  } catch {
    // Без сети число остаётся в браузере и уедет с профилем.
    return 'нет связи с кассой — баланс пока только в этом браузере';
  }
}

/** Сверять остатки при возврате на вкладку и раз в минуту. Возвращает отписку. */
export function watchBalances(): () => void {
  if (EXPERIMENTS || TRANSFERS) return () => {};
  void pullBalances();
  const onVis = (): void => {
    if (document.visibilityState === 'visible') void pullBalances();
  };
  document.addEventListener('visibilitychange', onVis);
  const timer = setInterval(() => {
    if (document.visibilityState === 'visible') void pullBalances();
  }, REFRESH_MS);
  return () => {
    document.removeEventListener('visibilitychange', onVis);
    clearInterval(timer);
  };
}
